import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bug, ImageUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BUG_REPORT_MAX_MESSAGE, useReportBug } from "@/hooks/useReportBug";
import {
  SCREENSHOT_ACCEPT,
  SCREENSHOT_LIMIT_PARAMS,
  SCREENSHOT_REJECTION_KEYS,
  checkScreenshot,
} from "@/utils/attachments";
import { toast } from "@/utils/toast";

/**
 * "Report a bug", controlled from outside (PRD I3, NAV-75).
 *
 * The dialog owns the form and the write; whoever renders it owns the control
 * that opens it. That is what lets the same dialog sit behind the account menu
 * — which is on screen in both shells, so the control is reachable from every
 * screen — and behind the button on the help page, without a context or a
 * global store for one boolean.
 *
 * **What it asks for, and what it says about it** (Jira NAV-141). One box of
 * prose and one optional screenshot. Everything else about the report — who,
 * where, when, which language, which browser, whether an admin was viewing the
 * app as somebody else — is captured by the hook, because a form that asked for
 * it would be asking people to describe their own browser. The dialog used to
 * *list* all of that, and to explain that the team reads reports on the
 * administration side and that nobody needs to know who to email; neither
 * sentence changed what a reporter does, and the second answered a question the
 * dialog itself had already settled by existing.
 */
export function ReportBugDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(["help", "common"]);
  const { report, loading } = useReportBug();
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  // So clearing a chosen file also clears the input's own value: without it,
  // picking the same file again after removing it fires no `change` event and
  // the dialog looks broken.
  const picker = useRef<HTMLInputElement>(null);

  const clearScreenshot = () => {
    setScreenshot(null);
    if (picker.current) picker.current.value = "";
  };

  /** Refused at the picker, by the rule the boundary will apply anyway — so the
   *  person is told which formats work while they still have the dialog open,
   *  rather than being told nothing and having the report arrive without the
   *  picture. */
  const choose = (file: File | undefined) => {
    if (!file) return;
    const check = checkScreenshot(file);
    if (!check.ok) {
      toast.warning(
        t(SCREENSHOT_REJECTION_KEYS[check.reason], SCREENSHOT_LIMIT_PARAMS),
      );
      clearScreenshot();
      return;
    }
    setScreenshot(file);
  };

  const submit = async () => {
    try {
      const { screenshotAttached } = await report(message, screenshot);
      // Two outcomes, and the difference matters to whoever is about to walk
      // away: the report always landed, and the screenshot may not have.
      if (screenshot && !screenshotAttached) {
        toast.warning(t("reportBug.sentWithoutScreenshot"));
      } else {
        toast.success(t("reportBug.sent"));
      }
      setMessage("");
      clearScreenshot();
      onOpenChange(false);
    } catch {
      // The hook has already logged it. What matters on screen is that the
      // report did NOT land — a dialog that closed on a failed write would
      // read exactly like one that succeeded.
      toast.warning(t("reportBug.failed"));
    }
  };

  return (
    // Dismissing keeps the text on purpose — somebody interrupted mid-sentence
    // should not have to retype what they were describing. It is cleared only
    // once the report has actually been sent.
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            {t("reportBug.title")}
          </DialogTitle>
          <DialogDescription>{t("reportBug.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="bug-report-message">{t("reportBug.label")}</Label>
          <Textarea
            id="bug-report-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={BUG_REPORT_MAX_MESSAGE}
            rows={6}
            placeholder={t("reportBug.placeholder")}
            data-testid="input-bug-report-message"
          />
        </div>

        {/* Usually the fastest way to show what went wrong, and the one thing a
            reporter could not send (Jira NAV-141). Optional and said to be: a
            report with no picture is still a report, and the box above is the
            part that has to be filled in. */}
        <div className="space-y-2">
          <Label htmlFor="bug-report-screenshot" className="flex items-center gap-2">
            <ImageUp className="h-4 w-4 text-muted-foreground" />
            {t("reportBug.screenshotLabel")}
          </Label>
          {screenshot ? (
            <div
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm"
              data-testid="bug-report-screenshot-chosen"
            >
              <span className="min-w-0 truncate">{screenshot.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearScreenshot}
                data-testid="button-remove-bug-report-screenshot"
              >
                <X className="mr-1 h-3.5 w-3.5" />
                {t("common:remove")}
              </Button>
            </div>
          ) : (
            <input
              ref={picker}
              id="bug-report-screenshot"
              type="file"
              accept={SCREENSHOT_ACCEPT}
              onChange={(event) => choose(event.target.files?.[0])}
              className="block w-full cursor-pointer rounded-lg border border-border bg-background p-2 text-sm"
              data-testid="input-bug-report-screenshot"
            />
          )}
          <p className="text-xs text-muted-foreground">
            {t("reportBug.screenshotHint", SCREENSHOT_LIMIT_PARAMS)}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("reportBug.cancel")}
          </Button>
          <Button
            className="bg-hb-navy text-white hover:bg-hb-navy/90"
            disabled={loading || message.trim().length === 0}
            onClick={() => void submit()}
            data-testid="button-confirm-bug-report"
          >
            {loading ? t("reportBug.sending") : t("reportBug.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The help page's own way in: a button beside the contact details, and the
 * dialog it opens.
 */
export function ReportBugButton() {
  const { t } = useTranslation("help");
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        onClick={() => setOpen(true)}
        data-testid="button-report-bug"
      >
        <Bug className="h-4 w-4" />
        {t("reportBug.title")}
      </Button>
      <ReportBugDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
